import { PropsWithChildren, useEffect, useMemo, useState } from 'react'
import { useForm, SubmitHandler } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { toast } from 'react-toastify'
import clsx from 'clsx'
import { FormTextField } from 'components/FormTextField'
import { useBranding } from 'utils/useBranding'
import { ComapnySettingModel } from 'utils/models/Company'
import { InitialValues } from './InitialValues'
import { ValidationSchema } from './ValidationSchema'
import { BrandingModel, OrganisationSettingModel } from 'api/data-contracts'
import ImageDragAndDrop from './ImageDragAndDrop'
import { Fileprops } from 'utils/models/File'
import BannerPreview from '../previews/BannerPreview'
import PreviewIntroPanel from '../previews/PreviewIntroPanel'
import PreviewContentPanel from '../previews/PreviewContentPanel'
import PreviewCenteredContentPanel from '../previews/PreviewCenteredContentPanel'
// API
import {
  useUpdateBannerImage,
  useDeleteBannerImage,
  useGetSettings,
  useUpdateOrganisationSettings,
  useGetOrganisation,
  useGetOrganisationID,
} from 'utils/organisation'
import { ActionButton } from 'components/ActionButton'

type PreviewButtonProps = {
  title: string
  branding: BrandingModel
  isOpen?: boolean
  onToggle?: () => void
}

function PreviewButton({
  title,
  children,
  branding,
  isOpen,
  onToggle,
}: PropsWithChildren<PreviewButtonProps>) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
  const showPreview = typeof isOpen === 'boolean' ? isOpen : uncontrolledOpen
  return (
    <>
      <button
        type="button"
        className={clsx([
          'flex items-center w-full justify-center px-4 hover:bg-white transition-colors rounded-t-3xl drop-shadow-2xl',
          showPreview && 'bg-white',
        ])}
        onClick={e => {
          e.preventDefault()
          if (onToggle) {
            onToggle()
          } else {
            setUncontrolledOpen(prev => !prev)
          }
        }}
      >
        <h3
          className="text-center flex-1"
          style={{ color: branding.stylingPrimary || '#3b82f6' }}
        >
          {title}
        </h3>
        <svg
          className={`w-5 h-5 transition-transform duration-200 ${
            showPreview ? 'rotate-90' : 'rotate-0'
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          style={{ color: branding.stylingPrimary || '#3b82f6' }}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5l7 7-7 7"
          />
        </svg>
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          showPreview ? 'max-h-144 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="drop-shadow-2xl">{children}</div>
      </div>
    </>
  )
}

const PlatformSetting = () => {
  const { branding } = useBranding()
  const { organisationId } = useGetOrganisationID()

  const { mutateAsync: updateBannerImage } = useUpdateBannerImage()
  const { mutateAsync: deleteBannerImage } = useDeleteBannerImage()
  const { settingData } = useGetSettings()
  const { mutateAsync: updateOrgSettings } = useUpdateOrganisationSettings()
  const { getOrganisationData } = useGetOrganisation()

  const initalValues = useMemo(() => {
    return InitialValues
  }, [])

  const [platformSetting, setPlatformSetting] =
    useState<ComapnySettingModel>(initalValues)
  const [bannerImage, setBannerImage] = useState<Fileprops | null>(null)
  const [isBannerImageDeleted, setIsBannerImageDeleted] = useState(false)
  const [saveSettingLoader, setSaveSettingLoader] = useState(false)
  const [activePreview, setActivePreview] = useState<
    'banner' | 'intro' | 'moreInfo' | 'howItWorks' | null
  >(null)

  useEffect(() => {
    setPlatformSetting({
      ...settingData,
      ...getOrganisationData,
    })
  }, [settingData, getOrganisationData])

  const {
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm<ComapnySettingModel>({
    resolver: yupResolver(ValidationSchema),
    defaultValues: useMemo(() => {
      return platformSetting
    }, [platformSetting]),
  })

  useEffect(() => {
    reset(platformSetting)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platformSetting])

  // Watch banner form fields for real-time preview updates
  const watchedFields = watch([
    'bannerTitle',
    'bannerSubTitle',
    'bannerCallToActionText',
    'bannerCallToActionUrl',
    'bannerImage',
  ])

  // Watch intro form fields for real-time preview updates
  const watchedIntroFields = watch(['introTitle', 'introText'])

  // Watch more info form fields for real-time preview updates
  const watchedMoreInfoFields = watch([
    'moreInfoTitle',
    'moreInfoDescription',
    'moreInfoCallToActionText',
    'moreInfoCallToActionUrl',
  ])

  // Controlled preview toggling handled inline where needed

  const onSubmit: SubmitHandler<ComapnySettingModel> = async data => {
    const updateOrgSettingData: OrganisationSettingModel = {
      bannerTitle: data.bannerTitle,
      bannerImage: data.bannerImage,
      bannerSubTitle: data.bannerSubTitle,
      bannerCallToActionText: data.bannerCallToActionText,
      bannerCallToActionUrl: data.bannerCallToActionUrl,
      introTitle: data.introTitle,
      introText: data.introText,
      howItWorksTitle: data.howItWorksTitle,
      howItWorks: data.howItWorks,
      howItWorksTitle2: data.howItWorksTitle2,
      howItWorks2: data.howItWorks2,
      howItWorksTitle3: data.howItWorksTitle3,
      howItWorks3: data.howItWorks3,
      howItWorksTitle4: data.howItWorksTitle4,
      howItWorks4: data.howItWorks4,
      moreInfoTitle: data.moreInfoTitle,
      moreInfoDescription: data.moreInfoDescription,
      moreInfoCallToActionText: data.moreInfoCallToActionText,
      moreInfoCallToActionUrl: data.moreInfoCallToActionUrl,
      focusTag: data.focusTag,
      companyInfoTitle: data.companyInfoTitle,
      companyInfo: data.companyInfo,
    }

    setSaveSettingLoader(true)
    try {
      if (organisationId !== '') {
        // Delete images
        if (isBannerImageDeleted) {
          await deleteBannerImage({
            organisationId,
          })
        }
        // Update images
        if (bannerImage !== null) {
          await updateBannerImage({
            organisationId,
            data: {
              bannerImage: bannerImage as File,
            },
          })
        }
        await updateOrgSettings({
          organisationId,
          data: updateOrgSettingData,
        })
      }
      toast.success('Platform settings saved')
    } catch {
      toast.error('Error saving platform settings')
    }
    setSaveSettingLoader(false)
  }

  const SettingsSaveButton = () => (
    <div className="flex justify-center mt-8">
      <ActionButton
        name="Save"
        background={branding.stylingPrimary ?? 'blue'}
        setAction={() => {
          setActivePreview(null)
          handleSubmit(onSubmit)()
        }}
        className={`flex justify-center ${
          saveSettingLoader && 'pointer-event-none opacity-50'
        }`}
        spinner={saveSettingLoader}
      />
    </div>
  )

  return (
    <>
      <form className="mb-50">
        <div className="flex w-full items-center">
          <h2 style={{ paddingRight: '10px' }}>Banner section</h2>
        </div>
        <div className="flex flex-wrap justify-between">
          <div className="w-full mb-25 desktop-w-50 min-w-500px">
            <p className="text-label">Banner image</p>
            <ImageDragAndDrop
              imageFile={bannerImage}
              setImageFile={setBannerImage}
              imageExist={platformSetting?.bannerImage}
              deleteStatus={isBannerImageDeleted}
              setDeleteImageStatus={setIsBannerImageDeleted}
              fileTypes={['JPG', 'PNG', 'JPEG']}
            />
          </div>
        </div>
        <div
          className="flex flex-wrap justify-between"
          onFocusCapture={() => setActivePreview('banner')}
        >
          <FormTextField
            control={control}
            errors={errors?.bannerTitle}
            name="bannerTitle"
            label="Banner title"
          />
          <FormTextField
            control={control}
            errors={errors?.bannerSubTitle}
            name="bannerSubTitle"
            label="Banner sub title"
            multilineValue={3}
          />
          <FormTextField
            control={control}
            errors={errors?.bannerCallToActionText}
            name="bannerCallToActionText"
            label="Banner call to action text"
          />
          <FormTextField
            control={control}
            errors={errors?.bannerCallToActionUrl}
            name="bannerCallToActionUrl"
            label="Banner call to action url"
            toolTipDescription="Choose a url for the call to action button. The url will start with the base url of the platform"
          />
        </div>
        <SettingsSaveButton />
        <div className="mt-8">
          <PreviewButton
            title="Banner Section Preview"
            branding={branding}
            isOpen={activePreview === 'banner'}
            onToggle={() =>
              setActivePreview(prev => (prev === 'banner' ? null : 'banner'))
            }
          >
            <div
              className="w-full rounded-lg overflow-hidden"
              style={{
                backgroundColor: `${branding.stylingPrimary || '#3b82f6'}05`,
                border: `2px dashed ${branding.stylingPrimary || '#3b82f6'}30`,
              }}
            >
              <BannerPreview
                bannerTitle={watchedFields[0] || undefined}
                bannerSubTitle={watchedFields[1] || undefined}
                bannerCallToActionText={watchedFields[2] || undefined}
                bannerCallToActionUrl={watchedFields[3] || undefined}
                bannerImageFile={bannerImage || undefined}
                bannerImageExist={platformSetting.bannerImage}
                bannerImageDeleteStatus={isBannerImageDeleted}
              />
            </div>
          </PreviewButton>
        </div>

        <div className="flex w-full items-center">
          <h2 style={{ paddingRight: '10px' }}>Intro section</h2>
        </div>

        <div
          className="flex flex-wrap justify-between"
          onFocusCapture={() => setActivePreview('intro')}
        >
          <FormTextField
            control={control}
            errors={errors?.introTitle}
            name="introTitle"
            label="Intro title"
            multilineValue={2}
          />
          <FormTextField
            control={control}
            errors={errors?.introText}
            name="introText"
            label="Intro text"
            multilineValue={4}
          />
        </div>
        <SettingsSaveButton />
        <div className="mt-8">
          <PreviewButton
            title="Intro Section Preview"
            branding={branding}
            isOpen={activePreview === 'intro'}
            onToggle={() =>
              setActivePreview(prev => (prev === 'intro' ? null : 'intro'))
            }
          >
            <div
              className="w-full rounded-lg overflow-hidden"
              style={{
                backgroundColor: `${branding.stylingPrimary || '#3b82f6'}05`,
                border: `2px dashed ${branding.stylingPrimary || '#3b82f6'}30`,
              }}
            >
              <PreviewIntroPanel
                introTitle={watchedIntroFields[0] || undefined}
                introText={watchedIntroFields[1] || undefined}
              />
            </div>
          </PreviewButton>
        </div>

        <div className="flex w-full items-center">
          <h2 style={{ paddingRight: '10px' }}>More info section</h2>
        </div>

        <div
          className="flex flex-wrap justify-between"
          onFocusCapture={() => setActivePreview('moreInfo')}
        >
          <FormTextField
            control={control}
            errors={errors?.moreInfoTitle}
            name="moreInfoTitle"
            label="More info title"
            multilineValue={2}
          />
          <FormTextField
            control={control}
            errors={errors?.moreInfoDescription}
            name="moreInfoDescription"
            label="More info description"
            multilineValue={4}
          />
          <FormTextField
            control={control}
            errors={errors?.moreInfoCallToActionText}
            name="moreInfoCallToActionText"
            label="More info call to action text"
          />
          <FormTextField
            control={control}
            errors={errors?.moreInfoCallToActionUrl}
            name="moreInfoCallToActionUrl"
            label="More info call to action url"
            toolTipDescription="Choose a url for the call to action button. The url will start with the base url of the platform"
          />
        </div>
        <SettingsSaveButton />
        <div className="mt-8">
          <PreviewButton
            title="More Info Section Preview"
            branding={branding}
            isOpen={activePreview === 'moreInfo'}
            onToggle={() =>
              setActivePreview(prev =>
                prev === 'moreInfo' ? null : 'moreInfo',
              )
            }
          >
            <div
              className="w-full rounded-lg overflow-hidden"
              style={{
                backgroundColor: `${branding.stylingPrimary || '#3b82f6'}05`,
                border: `2px dashed ${branding.stylingPrimary || '#3b82f6'}30`,
              }}
            >
              <PreviewContentPanel
                moreInfoTitle={watchedMoreInfoFields[0] || undefined}
                moreInfoDescription={watchedMoreInfoFields[1] || undefined}
                moreInfoCallToActionText={watchedMoreInfoFields[2] || undefined}
                moreInfoCallToActionUrl={watchedMoreInfoFields[3] || undefined}
              />
            </div>
          </PreviewButton>
        </div>

        <div className="flex w-full items-center">
          <h2 style={{ paddingRight: '10px' }}>How it works section</h2>
        </div>

        <div
          className="flex flex-wrap justify-between"
          onFocusCapture={() => setActivePreview('howItWorks')}
        >
          <FormTextField
            control={control}
            errors={errors?.howItWorksTitle}
            name="howItWorksTitle"
            label="How it works title"
            multilineValue={2}
          />
          <FormTextField
            control={control}
            errors={errors?.howItWorks}
            name="howItWorks"
            label="How it works"
            multilineValue={4}
          />
          <FormTextField
            control={control}
            errors={errors?.howItWorksTitle2}
            name="howItWorksTitle2"
            label="How it works title 2"
            multilineValue={2}
          />
          <FormTextField
            control={control}
            errors={errors?.howItWorks2}
            name="howItWorks2"
            label="How it works 2"
            multilineValue={4}
          />
          <FormTextField
            control={control}
            errors={errors?.howItWorksTitle3}
            name="howItWorksTitle3"
            label="How it works title 3"
            multilineValue={2}
          />
          <FormTextField
            control={control}
            errors={errors?.howItWorks3}
            name="howItWorks3"
            label="How it works 3"
            multilineValue={4}
          />
          <FormTextField
            control={control}
            errors={errors?.howItWorksTitle4}
            name="howItWorksTitle4"
            label="How it works title 4"
            multilineValue={2}
          />
          <FormTextField
            control={control}
            errors={errors?.howItWorks4}
            name="howItWorks4"
            label="How it works 4"
            multilineValue={4}
          />
        </div>
        <SettingsSaveButton />
        <div className="mt-8">
          <PreviewButton
            title="How It Works Section Preview"
            branding={branding}
            isOpen={activePreview === 'howItWorks'}
            onToggle={() =>
              setActivePreview(prev =>
                prev === 'howItWorks' ? null : 'howItWorks',
              )
            }
          >
            <div
              className="w-full rounded-lg overflow-hidden"
              style={{
                backgroundColor: `${branding.stylingPrimary || '#3b82f6'}05`,
                border: `2px dashed ${branding.stylingPrimary || '#3b82f6'}30`,
              }}
            >
              <PreviewCenteredContentPanel
                howItWorksTitle={watch('howItWorksTitle') || undefined}
                howItWorks={watch('howItWorks') || undefined}
                howItWorksTitle2={watch('howItWorksTitle2') || undefined}
                howItWorks2={watch('howItWorks2') || undefined}
                howItWorksTitle3={watch('howItWorksTitle3') || undefined}
                howItWorks3={watch('howItWorks3') || undefined}
                howItWorksTitle4={watch('howItWorksTitle4') || undefined}
                howItWorks4={watch('howItWorks4') || undefined}
              />
            </div>
          </PreviewButton>
        </div>
      </form>
    </>
  )
}

export default PlatformSetting
